import flatten from 'lodash/flatten';
import omit from 'lodash/omit';

import { ActivityRecord } from '@appTypes/esb';
import {
  activityTypeForActivityTypeId,
  advancementStatuses,
} from '@shared/constants';

import personNameBuilder from './personNameBuilder';

const getActivityStatus = ({ isApproved }: { isApproved: boolean }) =>
  isApproved ? advancementStatuses.APPROVED : advancementStatuses.COMPLETED;

const isPendingItem = (status: string) =>
  status === advancementStatuses.COMPLETED ||
  status === advancementStatuses.COUNSELOR_APPROVED;

type ActivityTypeId = keyof typeof activityTypeForActivityTypeId;

const denormalizePersons = (
  persons: ActivityRecord['registeredAdults'],
  activity: Partial<ActivityRecord>,
) => {
  const { id: activityId, name: activityName } = activity;

  const activityType =
    activityTypeForActivityTypeId[activity.activityTypeId as ActivityTypeId];

  return persons.map(({ activityValues, ...person }) => {
    const leaderApprovedUserId = person.leaderApprovedId;
    const status = getActivityStatus(person);
    const isAdvancementPending = isPendingItem(status);
    return {
      ...person,
      ...activity,
      leaderApprovedUserId,
      activityRecords: [...activityValues],
      isAdvancementPending,
      activityId,
      activityName,
      activityType,
      name: personNameBuilder.short(person),
      status,
      date: activity.startDateTime,
    };
  });
};

const denormalizeActivityItems = (activityDto: ActivityRecord) => {
  const { registeredYouths, registeredAdults, ...activity } = omit(
    activityDto,
    'nonRegisteredOrgParticipants',
  );
  const youths = denormalizePersons(registeredYouths, activity);
  const adults = denormalizePersons(registeredAdults, activity);
  return [...adults, ...youths];
};

export type DenormalizedActivityItem = ReturnType<
  typeof denormalizeActivityItems
>[0];

const mapActivityItemsDtoToModel = (dto: ActivityRecord[]) =>
  flatten(dto.map(denormalizeActivityItems));

export default mapActivityItemsDtoToModel;
